import { delay } from 'redux-saga'
import normalize from 'jsonapi-normalizer'
import {
  takeLatest,
  takeEvery,
  take,
  fork,
  all,
  call,
  select,
  put,
} from 'redux-saga/effects'
import * as R from 'ramda'

import {
  buildUrl,
} from 'helpers/actionHelper'

import {
  getRequest,
  post,
  del,
} from 'helpers/RequestUtil'

import {
  countSelector,
  pageSizeSelector,
  currentPageSelector,
  searchValueSelector,
  editStateSelector,
} from './selectors'

import {
  detailIdStateSelector as idSelector,
} from 'containers/Product/View/selectors'

import {
  CHANGE_PARTNER_SITE_ENABLE,
  CHANGE_CURRENT_PAGE,
  CHANGE_PAGE_SIZE,
  CHANGE_SEARCH_VALUE,
  CHANGE_EDIT_VALUE,
  FETCH_PARTNER_SITES,
  POST_PARTNER_SITE,
  DELETE_PARTNER_SITE,
} from './constants'

import {
  updateEditValue,
  updatePageSize,
  updateCountSize,
  updateCurrentPage,
  updateSearchValue,
  clearPartnerSiteList,
  updatePartnerSiteList,
  fetchPartnerSitesStart,
  fetchPartnerSitesSuccess,
  fetchPartnerSitesError,
  startFetchPartnerSitesAsync,
  postPartnerSite as enablePartnerSite,
  postPartnerSiteStart,
  postPartnerSiteSuccess,
  postPartnerSiteError,
  startPostPartnerSiteAsync,
  deletePartnerSite as disablePartnerSite,
  deletePartnerSiteStart,
  deletePartnerSiteSuccess,
  deletePartnerSiteError,
  startDeletePartnerSiteAsync,
} from './actions'

import {
  updateTablesAction,
} from 'containers/Entity/actions'

function* postPartnerSite({id, productHierarchyId, partnerSiteId, ...value}) {
  try {
    yield put(startPostPartnerSiteAsync())
    yield put(postPartnerSiteStart())
    const data = yield call(
      post,
      'partner_sites',
      {
        productHierarchyId,
        partnerSiteId
      },
      '/api/fulcrum/product/partner_sites'
    )
    const mappingId = R.path(['result', 'partner_sites', '0'])(data)
    const normalized = {
      result: { product_partner_sites: [id] },
      entities: { product_partner_sites: { [id]: { ...value, id, mappingId, productHierarchyId, partnerSiteId } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(postPartnerSiteSuccess())
    return data
  } catch(error) {
    yield put(postPartnerSiteError(error))
  }
}

function* deletePartnerSite({id, mappingId, ...value}) {
  try {
    yield put(startDeletePartnerSiteAsync())
    yield put(deletePartnerSiteStart())
    const data = yield call(
      del,
      `/api/fulcrum/product/partner_sites/${mappingId}`
    )
    const normalized = {
      result: { product_partner_sites: [id] },
      entities: { product_partner_sites: { [id]: { id, mappingId: null, ...value } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(deletePartnerSiteSuccess())
    return data
  } catch(error) {
    yield put(deletePartnerSiteError(error))
  }
}

function* fetchPartnerSites(url) {
  try {
    yield put(startFetchPartnerSitesAsync())
    yield put(fetchPartnerSitesStart())
    const data = yield call(
      getRequest,
      url
    )
    const normalized = normalize(data)
    yield put(updateTablesAction(normalized))
    yield put(fetchPartnerSitesSuccess())
    return data
  } catch(error) {
    yield put(fetchPartnerSitesError(error))
  }
}

function* handlePostPartnerSite(action) {
  const { value } = action
  yield call(postPartnerSite, value)
}

function* handleDeletePartnerSite(action) {
  const { value } = action
  yield call(deletePartnerSite, value)
}

function* handleChangePartnerSiteEnable(action) {
  const { value } = action
  if (R.isNil(value.mappingId))
    yield put(enablePartnerSite(value))
  else
    yield put(disablePartnerSite(value))
}

function* handleFetchPartnerSites() {
  let params = []
  yield call(delay, 500)
  const edit = yield select(editStateSelector)
  const page = yield select(currentPageSelector)
  const size = yield select(pageSizeSelector)
  const search = yield select(searchValueSelector)
  const id = yield select(idSelector)

  if (!edit)
    params = [...params, { type: 'enable', value: true }]
  if (search)
    params = [...params, { type: 'search', value: search }]

  const url = buildUrl(`/api/fulcrum/products/${id}/partner_sites`, ...params)
    .concat(`&page[number]=${page}&page[size]=${size}`)

  const data = yield call(fetchPartnerSites, url)
  const normalized = normalize(data)
  const { result: { product_partner_sites } } = normalized
  yield put(updateCountSize(data.meta.totalCount))
  yield put(updatePartnerSiteList(product_partner_sites))
}

function* handleChangePageSize(action) {
  const { size } = action
  const page = yield select(currentPageSelector)
  const prevSize = yield select(pageSizeSelector)
  let newPage = Math.floor((page - 1) * prevSize / size)
  newPage = (newPage > 0)
    ? newPage
    : newPage + 1

  yield put(updatePageSize(size))
  yield put(updateCurrentPage(newPage))
  yield fork(handleFetchPartnerSites)
}

function* handleChangeCurrentPage(action) {
  const { value } = action
  const page = yield select(currentPageSelector)
  yield put(updateCurrentPage(page + value))
  yield fork(handleFetchPartnerSites)
}

function* handleSearchChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(clearPartnerSiteList())
  yield put(updateSearchValue(value))
  yield fork(handleFetchPartnerSites)
}

function* handleEditChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(updateEditValue(value))
  yield put(clearPartnerSiteList())
  yield fork(handleFetchPartnerSites)
}

function* watchPostPartnerSite() {
  yield takeEvery(POST_PARTNER_SITE, handlePostPartnerSite)
}

function* watchDeletePartnerSite() {
  yield takeEvery(DELETE_PARTNER_SITE, handleDeletePartnerSite)
}

function* watchChangePartnerSiteEnable() {
  yield takeEvery(CHANGE_PARTNER_SITE_ENABLE, handleChangePartnerSiteEnable)
}

function* watchFetchPartnerSites() {
  yield takeEvery(FETCH_PARTNER_SITES, handleFetchPartnerSites)
}

function* watchChangePageSize() {
  yield takeLatest(CHANGE_PAGE_SIZE, handleChangePageSize)
}

function* watchChangeCurrentPage() {
  yield takeLatest(CHANGE_CURRENT_PAGE, handleChangeCurrentPage)
}

function* watchSearchChange() {
  yield takeLatest(CHANGE_SEARCH_VALUE, handleSearchChange)
}

function* watchEditChange() {
  yield takeLatest(CHANGE_EDIT_VALUE, handleEditChange)
}

function* loadSagas() {
  yield all([
    fork(watchFetchPartnerSites),
    fork(watchChangePageSize),
    fork(watchChangeCurrentPage),
    fork(watchSearchChange),
    fork(watchPostPartnerSite),
    fork(watchDeletePartnerSite),
    fork(watchChangePartnerSiteEnable),
    fork(watchEditChange)
  ])
}

export default loadSagas
